import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  COMMITMENTS, fmt, fmtPct, commitmentTypeLabel, groupCommitmentsByRoute, routeBlurb
} from "../../data/portalDemo.js";
import { fetchDocuments, downloadUrl } from "../../lib/portalApi.js";

export default function PortalDashboard() {
  // Per-currency aggregates for the KPI rail at the top
  const groups = {};
  COMMITMENTS.forEach(c => {
    const k = c.currency;
    groups[k] = groups[k] || { committed: 0, called: 0, contributed: 0, distributed: 0, nav: 0, count: 0 };
    groups[k].committed   += c.committed;
    groups[k].called      += c.called;
    groups[k].contributed += c.contributedPaid;
    groups[k].distributed += c.distributed;
    groups[k].nav         += c.nav;
    groups[k].count       += 1;
  });

  // Recent documents from live Box feed
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchDocuments().then(d => { if (alive) setDocs(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const recent = (docs || [])
    .slice()
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 5);

  // Group commitments by route
  const routeGroups = groupCommitmentsByRoute();

  return (
    <div className="portal-page">
      <h1 className="portal-h1">Portfolio</h1>

      {/* KPI rail — one block per currency */}
      <div className="kpi-rail">
        {Object.entries(groups).map(([ccy, g]) => {
          const tvpi = g.called > 0 ? (g.nav + g.distributed) / g.called : 0;
          const dpi  = g.called > 0 ? g.distributed / g.called : 0;
          return (
            <div className="kpi-block" key={ccy}>
              <div className="kpi-block-head">
                <span className="kpi-ccy">{ccy}</span>
                <span className="kpi-funds">{g.count} commitment{g.count > 1 ? "s" : ""}</span>
              </div>
              <div className="kpi-grid">
                <Kpi label="Committed"   value={fmt(g.committed,   ccy)} />
                <Kpi label="Called"      value={fmt(g.called,      ccy)} />
                <Kpi label="Contributed" value={fmt(g.contributed, ccy)} />
                <Kpi label="Distributed" value={fmt(g.distributed, ccy)} />
                <Kpi label="NAV"         value={fmt(g.nav,         ccy)} highlight />
                <Kpi label="DPI"         value={dpi.toFixed(2) + "x"} />
                <Kpi label="TVPI"        value={tvpi.toFixed(2) + "x"} />
                <Kpi label="Unfunded"    value={fmt(g.committed - g.called, ccy)} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Sections per investor route */}
      {Array.from(routeGroups.entries()).map(([route, rows]) => (
        <section key={route} className="route-section">
          <div className="route-section-head">
            <h2 className="portal-h2">{commitmentTypeLabel(route)}</h2>
            <p className="muted small" style={{ marginTop: -6, marginBottom: 14 }}>{routeBlurb(route)}</p>
          </div>
          <FundTable rows={rows} />
        </section>
      ))}

      <div className="portal-two-col">
        <section>
          <h2 className="portal-h2">Recent documents</h2>
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(d => (
                  <tr key={d.id}>
                    <td><a className="portal-link" href={downloadUrl(d.id)} target="_blank" rel="noopener noreferrer">{d.title}</a></td>
                    <td className="muted">{d.type}</td>
                    <td className="muted">{d.date}</td>
                    <td><Link className="portal-rowlink" to="/portal/documents">View all →</Link></td>
                  </tr>
                ))}
                {!docs && (
                  <tr><td colSpan={4} className="muted small">Loading from Box…</td></tr>
                )}
                {docs && !recent.length && (
                  <tr><td colSpan={4} className="muted small">No documents yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="portal-aside">
          <h3 className="portal-h3">Five routes to invest with Daksfirst</h3>
          <p className="muted small"><b>UK Warehouse · Pool B-Note</b> — B-Note position on the warehouse pool, diversified across the bridging book, subordinated to the senior wholesale facility.</p>
          <p className="muted small"><b>UK Whole-Loan · Single-deal B-Note</b> — pick deals 1-by-1, B-Note on each loan, subordinated to the senior wholesale facility on that deal.</p>
          <p className="muted small"><b>Hospitality</b> — senior or mezz on operating hotel real estate.</p>
          <p className="muted small"><b>India · Lux Feeder</b> — EU-wrapped exposure to Indian credit.</p>
          <p className="muted small"><b>India · NCDs direct</b> — direct subscription to listed/private NCDs.</p>
          <hr/>
          <h3 className="portal-h3">Disclosure</h3>
          <p className="muted small">
            Wealth-management mandates are entered into directly with Maystone Capital
            Limited (FCA FRN 758412). Daksfirst Limited operates as Maystone&rsquo;s
            Appointed Representative for credit and fund-management activities.
            Daksfirst AR FRN 937220.
          </p>
        </aside>
      </div>
    </div>
  );
}

function FundTable({ rows }) {
  return (
    <div className="portal-table-wrap">
      <table className="portal-table">
        <thead>
          <tr>
            <th>Investment</th>
            <th>Vehicle</th>
            <th>Vintage</th>
            <th className="num">Committed</th>
            <th className="num">Drawn</th>
            <th className="num">Distributed</th>
            <th className="num">NAV / Mark</th>
            <th className="num">Net IRR</th>
            <th className="num">TVPI</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.slug}>
              <td>
                <Link className="portal-link" to={`/portal/funds/${c.slug}`}>{c.fund}</Link>
              </td>
              <td className="muted">{c.vehicle}</td>
              <td>{c.vintage}</td>
              <td className="num">{fmt(c.committed,        c.currency)}</td>
              <td className="num">{fmt(c.called,           c.currency)}</td>
              <td className="num">{fmt(c.distributed,      c.currency)}</td>
              <td className="num strong">{fmt(c.nav,       c.currency)}</td>
              <td className="num">{fmtPct(c.netIRR)}</td>
              <td className="num">{c.tvpi.toFixed(2)}x</td>
              <td><Link className="portal-rowlink" to={`/portal/funds/${c.slug}`}>Open →</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ label, value, highlight }) {
  return (
    <div className={"kpi" + (highlight ? " kpi-strong" : "")}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}
